import React, { useEffect, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { PromptFooter } from "components/DealGroupedTable/PromptFooter";

interface TableSettingsProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** The Columns LIST — the checkbox rows currently shown. */
  optionsList: string[];
  /** All column titles that can be added to the list (from `getDealsTableColumnsActions`). */
  availableColumns: string[];
  /** Currently checked (visible) columns — a subset of the list. */
  selected: string[];
  /** Base columns that cannot be removed from the list. */
  baseColumns: string[];
  onApply: (selected: string[], optionsList: string[]) => void;
}

const checkboxSx = { p: "4px", color: "#bac7ce", "&.Mui-checked": { color: "#0a3142" } } as const;

export const TableSettings: React.FC<TableSettingsProps> = ({
  anchorEl,
  open,
  onClose,
  optionsList,
  availableColumns,
  selected,
  baseColumns,
  onApply
}) => {
  const [draftOptions, setDraftOptions] = useState<string[]>(optionsList);
  const [draftSelected, setDraftSelected] = useState<string[]>(selected);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [addSearch, setAddSearch] = useState("");

  // Re-sync the drafts each time the popover opens.
  useEffect(() => {
    if (open) {
      setDraftOptions(optionsList);
      setDraftSelected(selected);
    }
  }, [open, optionsList, selected]);

  const addableColumns = availableColumns.filter((name) => !draftOptions.includes(name));
  const filteredAddable = addableColumns.filter((name) =>
    name.toLowerCase().includes(addSearch.trim().toLowerCase())
  );

  const openAddPicker = (event: React.MouseEvent<HTMLElement>) => {
    setAddSearch("");
    setAddAnchor(event.currentTarget);
  };

  const toggleSelected = (name: string) => {
    setDraftSelected((prev) => (prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]));
  };

  // Add to the list and keep the picker open (with the search reset) for quick multi-add.
  const addColumn = (name: string) => {
    setDraftOptions((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setAddSearch("");
  };

  // Only user-added (non-base) columns can be removed from the list.
  const removeOption = (name: string) => {
    setDraftOptions((prev) => prev.filter((item) => item !== name));
    setDraftSelected((prev) => prev.filter((item) => item !== name));
  };

  const handleApply = () => {
    // Keep only checked names that still exist in the list.
    onApply(
      draftSelected.filter((name) => draftOptions.includes(name)),
      draftOptions
    );
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { width: 380, borderRadius: "12px", p: "24px" } } }}
    >
      <Typography sx={{ fontSize: "18px", fontWeight: "400", color: "#0a3142", mb: "16px" }}>Columns</Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: "10px",
          maxHeight: "200px",
          overflow: "auto"
        }}
      >
        {draftOptions.map((option) => {
          const checked = draftSelected.includes(option);
          const removable = !baseColumns.includes(option);
          return (
            <Stack key={option} direction="row" alignItems="center" sx={{ height: "34px", minWidth: 0 }}>
              <Checkbox size="small" checked={checked} onChange={() => toggleSelected(option)} sx={checkboxSx} />
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#0a3142",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {option}
              </Typography>
              {removable && (
                <IconButton onClick={() => removeOption(option)} sx={{ p: "2px", width: "20px", height: "20px" }}>
                  <CloseIcon sx={{ fontSize: "14px", color: "#92a7bc" }} />
                </IconButton>
              )}
            </Stack>
          );
        })}
      </Box>

      <Button
        startIcon={<AddCircleOutlineIcon sx={{ fontSize: "18px" }} />}
        disabled={!addableColumns.length}
        onClick={openAddPicker}
        sx={{
          mt: "8px",
          textTransform: "none",
          fontSize: "13px",
          color: "#0a3142",
          bgcolor: "#0a31420a",
          borderRadius: "20px",
          px: "12px",
          "&:hover": { bgcolor: "#0a314214" }
        }}
      >
        Add Characteristic
      </Button>
      <Popover
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 260, mt: "4px", borderRadius: "10px", p: "8px" } } }}
      >
        <TextField
          autoFocus
          fullWidth
          placeholder="Search characteristics"
          value={addSearch}
          onChange={(e) => setAddSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#92a7bc" }} />
              </InputAdornment>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: "8px", height: "34px", fontSize: "13px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe8f5" }
          }}
        />
        <Box sx={{ maxHeight: 200, overflow: "auto", mt: "6px" }}>
          {filteredAddable.length ? (
            filteredAddable.map((name) => (
              <MenuItem
                key={name}
                onClick={() => addColumn(name)}
                sx={{
                  fontSize: "13px",
                  color: "#0a3142",
                  borderRadius: "6px",
                  px: "8px",
                  minHeight: "32px",
                  "&:hover": { bgcolor: "#0a31420a" }
                }}
              >
                {name}
              </MenuItem>
            ))
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#92a7bc", p: "8px" }}>No characteristics found</Typography>
          )}
        </Box>
      </Popover>

      <Box sx={{ mt: "16px" }}>
        <PromptFooter onCancel={onClose} onApply={handleApply} />
      </Box>
    </Popover>
  );
};
